import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate, type NavigateFunction } from "react-router-dom";
import { useAuth, authPrincipalKey } from "./AuthProvider";
import { useToast } from "./ToastProvider";
import { useGeneratingPlans } from "../hooks/useGeneratingPlans";
import type { PendingPlanResolution } from "../hooks/useGeneratingPlanWatcher";
import { fuelPlanHref, planPendingHref } from "../lib/navPaths";
import {
  publishPlanGenerationEvent,
  type PlanGenerationEvent,
  type PlanGenerationMeta
} from "../lib/planGenerationBus";
import {
  loadNotifications,
  markNotificationsSeen,
  waitForNotifications
} from "../lib/notificationsApi";
import type { NotificationsResponse } from "../lib/notificationsApi";
import { isAbortError } from "../lib/api";
import { isPlanNotificationAcknowledged } from "../lib/planNotificationAck";
import {
  PlanGenerationContext,
  type PlanGenerationContextValue
} from "./planGenerationContext";

/** A settled outcome, rendered as a toast descriptor. */
type OutcomeToast = {
  tone: "success" | "error" | "info" | "warning";
  message: string;
  detail?: string;
  durationMs?: number;
  action?: { label: string; onClick: (toastId: string) => void };
};

type TerminalOutcome = "live" | "superseded" | "failed" | "timeout" | "auth_blocked";

/**
 * The ONE mapper from a terminal outcome → toast copy (impl note #5). Both the
 * live watcher (`handleResolved`) and the boot drain (`§2.3`) route through it,
 * so the two directions can never drift on wording. Pure: no refs, no
 * side-effects except the click handlers it closes over `navigate` for.
 *
 * `source` reproduces the live path's "box" success wording; `fuelPlanId` is
 * required for a `live` action target. Returns `null` when there is nothing to
 * show (a `live` outcome with no plan id — impossible for a real completion,
 * but keeps the caller total).
 */
export function resolveTerminalOutcome(
  outcome: TerminalOutcome,
  args: {
    fuelPlanId: string | null;
    label?: string | null;
    failureSummary?: string | null;
    source?: string;
    navigate: NavigateFunction;
  }
): OutcomeToast | null {
  const { fuelPlanId, label, failureSummary, source, navigate } = args;
  const detail = label ?? undefined;

  if (outcome === "live") {
    if (!fuelPlanId) return null;
    const successMessage =
      source === "box" ? "Your fuel box is ready 🎉" : "Your fueling plan is ready";
    return {
      tone: "success",
      message: successMessage,
      detail,
      durationMs: 0,
      action: {
        label: "View plan",
        onClick: () => navigate(fuelPlanHref(fuelPlanId))
      }
    };
  }
  if (outcome === "superseded") {
    return {
      tone: "info",
      message: "That fueling plan was replaced by a newer one",
      detail,
      durationMs: 0,
      action: { label: "View plans", onClick: () => navigate("/plans") }
    };
  }
  if (outcome === "timeout") {
    return {
      tone: "info",
      message: "Your plan is taking longer than usual",
      detail: "It's still building — we'll email you when it's ready.",
      durationMs: 0
    };
  }
  if (outcome === "auth_blocked") {
    return {
      tone: "warning",
      message: "Sign-in needed to finish your fueling plan",
      detail: "Refresh the page to pick it back up.",
      durationMs: 0
    };
  }
  // failed / expired
  return {
    tone: "error",
    message: "We couldn't build your fueling plan",
    detail: failureSummary ?? label ?? "Try regenerating again.",
    durationMs: 0
  };
}

/** Minimum gap between drains re-armed by a visibility regain (§2.3). */
const DRAIN_THROTTLE_MS = 5 * 60_000;

/** Minimum gap between plan-history reloads re-armed by a visibility regain
 *  (§3.1) — closes the "build dispatched on another tab/device" gap. */
const HISTORY_RELOAD_THROTTLE_MS = 60_000;

/** localStorage key the storefront poll (Phase 2b) + long-poll (Phase 4) gate
 *  on: set to an expiry epoch-ms while a build is pending, cleared otherwise. */
const WATCH_FLAG_KEY = "fg:plan-generation:watch";
/** How long the watch flag stays live after `hasPending` last went true. */
const WATCH_FLAG_TTL_MS = 20 * 60_000;

/** Long-poll hold (§4.2) — matches the server cap. */
const LONG_POLL_HOLD_MS = 20_000;
/** Back-off after a long-poll iteration errors (not an abort) before retrying,
 *  so a flapping network / restarting server doesn't hot-loop. */
const LONG_POLL_BACKOFF_MS = 4_000;
/** "Unseen recently" TTL (impl note "unseen recently"): the long-poll keeps
 *  looping for this long after the last non-empty drain even with no pending
 *  build, so a slightly-late outcome still pushes rather than waiting for the
 *  next visibility drain. */
const LONG_POLL_UNSEEN_TTL_MS = 10 * 60_000;

/**
 * Advance the seen-watermark, retrying a couple of times with a short backoff.
 * The watermark is treated as advanced client-side regardless — this retry is
 * only so a transient POST failure doesn't re-toast everything on the next
 * boot. Swallows all errors (background convenience).
 */
async function markSeenWithRetry(
  seenAt: string,
  csrfToken: string,
  refreshCsrf: () => Promise<string>
): Promise<void> {
  const delays = [0, 1500];
  for (let i = 0; i < delays.length; i += 1) {
    if (delays[i] > 0) await new Promise((r) => setTimeout(r, delays[i]));
    try {
      await markNotificationsSeen(seenAt, csrfToken, refreshCsrf);
      return;
    } catch {
      // retry
    }
  }
}

/**
 * App-root coordinator for fuel-plan generation outcomes (docs: plan-generation
 * notifications, Phase 1). One always-mounted watcher over the plan history +
 * every `track()`ed request, so build outcomes surface no matter which page
 * the user is on:
 *
 *   - sticky, dismissable toasts for every terminal result ("Contract A",
 *     app-wide) — success carries a "View plan" action;
 *   - publishes each lifecycle event to the planGenerationBus (same-tab
 *     CustomEvent + cross-tab BroadcastChannel — the future storefront
 *     listener's feed);
 *   - a regenerate completing while the user is still on the SOURCE plan's
 *     page auto-navigates to the replacement (the old plan is now superseded).
 *
 * The provider is the single owner of generation-outcome toasts — dispatch
 * sites and per-screen watchers must not push their own. In-flight requests
 * survive reload because they're server-tracked: the internal
 * `useGeneratingPlans` re-discovers processing rows from plan history.
 */
export function PlanGenerationProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { state: authState, refreshCsrf } = useAuth();
  const { push, pushPending, update, toasts } = useToast();
  // Snapshot for event-driven reads (settleToast) without re-binding callbacks.
  const toastsRef = useRef(toasts);
  toastsRef.current = toasts;

  // Latest pathname without re-binding the resolve callback on every nav.
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;
  const principalKeyRef = useRef<string | null>(null);
  principalKeyRef.current = authPrincipalKey(authState);

  // Per-request bookkeeping. Maps live in refs (event-driven, no re-render
  // needed); `inFlight` is state because button-disabling UI derives from it.
  const metaRef = useRef(
    new Map<string, { meta: PlanGenerationMeta; startedAt: string; pendingToastId: string | null }>()
  );
  // Authoritative terminals only (completed | failed | expired | superseded |
  // auth_blocked) — these filter notifications forever (CT-3).
  const resolvedRef = useRef(new Set<string>());
  // Client give-ups (foreground poll `timeout`). These clear the spinner but do
  // NOT filter later notifications, and keep the long-poll hub alive until an
  // authoritative terminal promotes the id into `resolvedRef` (CT-3 / CT-5).
  const timedOutRef = useRef(new Set<string>());
  // Bumped whenever a give-up adds to `timedOutRef` so the long-poll effect
  // re-evaluates its start condition — the hub must (re)spin up to deliver the
  // eventual authoritative completion even when `hasPending` is already false.
  const [timeoutEpoch, setTimeoutEpoch] = useState(0);
  const [inFlight, setInFlight] = useState<ReadonlySet<string>>(() => new Set());

  const publish = useCallback(
    (
      type: PlanGenerationEvent["type"],
      requestId: string,
      extra?: Pick<PlanGenerationEvent, "fuelPlanId" | "failureSummary">
    ) => {
      const entry = metaRef.current.get(requestId);
      publishPlanGenerationEvent({
        v: 1,
        type,
        requestId,
        fuelPlanId: extra?.fuelPlanId ?? null,
        failureSummary: extra?.failureSummary ?? null,
        principalKey: principalKeyRef.current ?? "anon",
        startedAt: entry?.startedAt ?? null,
        at: new Date().toISOString(),
        meta: entry?.meta ?? {}
      });
    },
    []
  );

  /** Push a fresh toast, or flip this request's pending toast in place. */
  const settleToast = useCallback(
    (
      requestId: string,
      toast: {
        tone: "success" | "error" | "info" | "warning";
        message: string;
        detail?: string;
        durationMs?: number;
        action?: { label: string; onClick: (toastId: string) => void };
      }
    ) => {
      const entry = metaRef.current.get(requestId);
      const pendingId = entry?.pendingToastId;
      // Flip the pending toast in place when it's still on screen; if the
      // user dismissed it (or there never was one) push a fresh outcome toast
      // — `update` on a gone id would silently swallow the result.
      if (pendingId && toastsRef.current.some((t) => t.id === pendingId)) {
        update(pendingId, toast);
      } else {
        push(toast);
      }
    },
    [push, update]
  );

  // `reloadRows` is defined below (returned by useGeneratingPlans, which takes
  // handleResolved). The settle helpers need it, so read it via a ref.
  const reloadRowsRef = useRef<() => void>(() => {});

  /**
   * CT-5 — the ONE authoritative terminal settle, shared by BOTH the live
   * watcher (`handleResolved`) and the long-poll hub (`processNotifications`).
   * Whichever path first learns the authoritative outcome does the SAME work:
   * toast + bus publish + row/journey refetch. Idempotent on `resolvedRef`, so
   * the other path early-returns (settle exactly once).
   *
   * `timeout` is NOT an authoritative terminal and must never flow through here
   * (that was C2 — a give-up poisoning `resolvedRef`); it uses `settleGiveUp`.
   */
  const settleAuthoritativeTerminal = useCallback(
    (
      requestId: string,
      outcome: "completed" | "superseded" | "failed" | "expired" | "auth_blocked",
      opts: {
        fuelPlanId: string | null;
        failureSummary?: string | null;
        toast: OutcomeToast | null;
        /** Skip the navigate that `handleResolved` does inline (it already
         *  navigated + toasted); the hub path passes false. */
        alreadyToasted?: boolean;
      }
    ): boolean => {
      // (a) Idempotent — whichever of watcher/hub wins first settles once.
      if (resolvedRef.current.has(requestId)) return false;
      resolvedRef.current.add(requestId);

      // (b) toast (caller may have already flipped its own pending toast).
      if (!opts.alreadyToasted && opts.toast) settleToast(requestId, opts.toast);

      // (c) publish the outcome to the bus.
      publish(outcome === "completed" ? "completed" : outcome, requestId, {
        fuelPlanId: opts.fuelPlanId,
        failureSummary: opts.failureSummary ?? null
      });

      // (d) refetch plan history; bus subscribers refetch the journey/box.
      reloadRowsRef.current();

      // (e) clear from inFlight + timedOutRef.
      setInFlight((curr) => {
        if (!curr.has(requestId)) return curr;
        const next = new Set(curr);
        next.delete(requestId);
        return next;
      });
      timedOutRef.current.delete(requestId);
      return true;
    },
    [publish, settleToast]
  );

  /**
   * CT-5 — the foreground `timeout` give-up ONLY. Clears the spinner and tells
   * the user we're still building, but does NOT record an authoritative
   * terminal: no `resolvedRef`, no bus publish, no navigation. Adds to
   * `timedOutRef` so the long-poll hub stays alive and a later authoritative
   * notification promotes the id via `settleAuthoritativeTerminal`.
   */
  const settleGiveUp = useCallback(
    (requestId: string) => {
      // A prior authoritative terminal already settled this id — nothing to do.
      if (resolvedRef.current.has(requestId)) return;
      timedOutRef.current.add(requestId);
      // Nudge the long-poll effect to re-evaluate (hub-liveness across the
      // T+10→T+18 gap) even when `hasPending` doesn't change.
      setTimeoutEpoch((n) => n + 1);
      setInFlight((curr) => {
        if (!curr.has(requestId)) return curr;
        const next = new Set(curr);
        next.delete(requestId);
        return next;
      });
      const label = metaRef.current.get(requestId)?.meta.label;
      const toast = resolveTerminalOutcome("timeout", {
        fuelPlanId: null,
        label,
        navigate
      });
      if (toast) settleToast(requestId, toast);
    },
    [navigate, settleToast]
  );

  const handleResolved = useCallback(
    (info: PendingPlanResolution) => {
      // A client give-up ("stopped watching") is NOT a terminal — hand off to
      // the long-poll hub (CT-5). Never seed `resolvedRef` from a timeout.
      if (info.result === "timeout") {
        settleGiveUp(info.requestId);
        return;
      }

      // The watcher can re-emit across effect re-runs; toast/publish once.
      if (resolvedRef.current.has(info.requestId)) return;

      const entry = metaRef.current.get(info.requestId);
      const label = entry?.meta.label;

      // The user is staring at this request's full-screen pending state —
      // PlanPending renders its own superseded/failed copy, so a parallel toast
      // for the same outcome would double-surface it. Success still flows below
      // (short confirmation after the navigate). We still settle authoritatively
      // (bus + reload) so the box CTA flips; only the toast is suppressed.
      const onOwnPendingScreen = pathnameRef.current === planPendingHref(info.requestId);
      if (onOwnPendingScreen && info.result !== "completed") {
        const outcome = info.result === "expired" ? "failed" : info.result;
        settleAuthoritativeTerminal(info.requestId, outcome, {
          fuelPlanId: info.fuelPlanId,
          failureSummary: info.failureSummary ?? null,
          toast: null,
          alreadyToasted: true
        });
        return;
      }

      const source = entry?.meta.source;

      if (info.result === "completed" && info.fuelPlanId) {
        const fuelPlanId = info.fuelPlanId;
        const sourcePlanId = entry?.meta.sourcePlanId;
        const onSourcePlanPage =
          sourcePlanId != null && pathnameRef.current === fuelPlanHref(sourcePlanId);
        const alreadyViewing = pathnameRef.current === fuelPlanHref(fuelPlanId);
        if (onSourcePlanPage || alreadyViewing) {
          // The plan under the user's feet was just superseded by this build —
          // move them onto the replacement (no-op when they're already viewing
          // it, e.g. the PlanPending screen navigated first); short
          // confirmation toast rather than a sticky one they'd have to dismiss.
          const settled = settleAuthoritativeTerminal(info.requestId, "completed", {
            fuelPlanId,
            toast: null,
            alreadyToasted: true
          });
          if (!settled) return;
          navigate(fuelPlanHref(fuelPlanId), { replace: true });
          const successMessage =
            source === "box" ? "Your fuel box is ready 🎉" : "Your fueling plan is ready";
          settleToast(info.requestId, {
            tone: "success",
            message: successMessage,
            detail: label
          });
          return;
        }
        // Sticky (dismiss-only) so a user who stepped away can't miss it.
        const toast = resolveTerminalOutcome("live", { fuelPlanId, label, source, navigate });
        settleAuthoritativeTerminal(info.requestId, "completed", { fuelPlanId, toast });
        return;
      }

      // superseded / auth_blocked / failed / expired all map straight through
      // the shared terminal mapper (impl note #5). `expired` folds into the
      // failed branch there, matching the pre-refactor behaviour. (`timeout`
      // was handled at the top via settleGiveUp.)
      const outcome = info.result === "expired" ? "failed" : info.result;
      const mapped =
        info.result === "superseded"
          ? resolveTerminalOutcome("superseded", { fuelPlanId: info.fuelPlanId, label, navigate })
          : info.result === "auth_blocked"
            ? resolveTerminalOutcome("auth_blocked", {
                fuelPlanId: info.fuelPlanId,
                label,
                navigate
              })
            : resolveTerminalOutcome("failed", {
                fuelPlanId: info.fuelPlanId,
                label,
                failureSummary: info.failureSummary ?? null,
                navigate
              });
      settleAuthoritativeTerminal(info.requestId, outcome, {
        fuelPlanId: info.fuelPlanId,
        failureSummary: info.failureSummary ?? null,
        toast: mapped
      });
    },
    [navigate, settleAuthoritativeTerminal, settleGiveUp, settleToast]
  );

  // The single app-wide watcher. `useGeneratingPlans` loads plan history and
  // re-discovers server-tracked in-flight rows, so builds survive reloads even
  // when nothing called track() this session. Inside the provider's own body
  // the context is still the no-op default, so the hook's auto-registration
  // doesn't recurse.
  const {
    track: watcherTrack,
    resetWatch: watcherResetWatch,
    rows,
    reload: reloadRows,
    justResolved,
    hasPending
  } = useGeneratingPlans(undefined, handleResolved, {
    // F-01-3: this provider mounts OUTSIDE the auth Gate, so gate the plan-history
    // load on a resolved principal — otherwise it races ahead of the context ping
    // and mints a competing anon session (the first-load 403s).
    principalKey: authPrincipalKey(authState)
  });
  reloadRowsRef.current = reloadRows;

  const track = useCallback<PlanGenerationContextValue["track"]>(
    (requestId, options) => {
      if (!requestId) return;
      // C3 re-arm (CT-3): a retry hands back the SAME still-processing
      // requestId. It's already in `metaRef` (so the plain early-return below
      // would swallow it) AND we gave up on it (`timedOutRef`) but it never
      // reached an authoritative terminal (`resolvedRef`). Re-arm the watcher's
      // foreground poll WITHOUT re-publishing "started" or a second pending
      // toast, and drop it from `timedOutRef` (the fresh clock owns it now).
      if (metaRef.current.has(requestId)) {
        if (timedOutRef.current.has(requestId) && !resolvedRef.current.has(requestId)) {
          timedOutRef.current.delete(requestId);
          setInFlight((curr) => {
            if (curr.has(requestId)) return curr;
            const next = new Set(curr);
            next.add(requestId);
            return next;
          });
          watcherResetWatch(requestId);
        }
        return;
      }
      const { pendingToast, ...meta } = options ?? {};
      const pendingToastId = pendingToast ? pushPending(pendingToast) : null;
      metaRef.current.set(requestId, {
        meta,
        startedAt: new Date().toISOString(),
        pendingToastId
      });
      setInFlight((curr) => {
        const next = new Set(curr);
        next.add(requestId);
        return next;
      });
      publish("started", requestId);
      watcherTrack(requestId);
    },
    [publish, pushPending, watcherResetWatch, watcherTrack]
  );

  const isPlanRegenerating = useCallback(
    (planId: string) => {
      for (const requestId of inFlight) {
        if (metaRef.current.get(requestId)?.meta.sourcePlanId === planId) return true;
      }
      return false;
    },
    [inFlight]
  );

  // Read the latest `inFlight` inside the drain without re-binding it on every
  // set-change (the drain is throttled + latched, not React-render-driven).
  const inFlightRef = useRef(inFlight);
  inFlightRef.current = inFlight;

  // ── Boot drain (§2.3): durable "you missed it" outcomes ──────────────────
  // A build that finished while this tab was closed (or on another device)
  // surfaces on the next visit as the SAME sticky toast the live watcher would
  // have shown. Server truth (`GET /api/notifications`) → shared terminal
  // mapper → toast; then advance the watermark so it never re-toasts.
  //
  // Latched per principal so a re-render / auth re-ping can't re-drain the same
  // account; re-armed on a visibility regain (throttled ≥5 min) to catch
  // "built while I was on another device" without a badge UI.
  const drainedPrincipalRef = useRef<string | null>(null);
  const lastDrainAtRef = useRef(0);

  // ── The ONE monotonic notification watermark (impl note #4) ──────────────
  // Both the boot/visibility drain AND the Phase 4 long-poll read + advance
  // THIS value: the drain uses it to filter "unseen", the long-poll passes it
  // as the `/wait` cursor. Seeded from the server `seenAt` on the first drain,
  // then advanced client-side whenever rows are processed (and pushed to the
  // server via markSeenWithRetry). The long-poll never uses a cursor staler
  // than the last drain, so it can't replay drained events.
  const watermarkRef = useRef<string | null>(null);
  // Timestamp of the last drain/long-poll iteration that surfaced ANY rows —
  // the long-poll's "unseen recently" idle-stop clock.
  const lastNonEmptyAtRef = useRef(0);

  /**
   * The shared row-processing path (impl note #5): filter a `/notifications`
   * or `/wait` response's rows to those newer than the watermark and not
   * already handled this session, toast them (1–2 individually, 3+ collapsed),
   * seed `resolvedRef` so the live watcher can't re-fire, and advance the
   * watermark (client-side + retried POST). Both the drain and the long-poll
   * feed the SAME function so the two directions can't drift.
   *
   * Returns the number of rows surfaced (the long-poll uses it to keep its
   * "unseen recently" TTL alive).
   */
  const processNotifications = useCallback(
    (res: NotificationsResponse): number => {
      // Seed the watermark from the server on first sight; thereafter the local
      // watermark is authoritative (it only ever moves forward).
      if (watermarkRef.current == null) watermarkRef.current = res.seenAt;
      const watermark = watermarkRef.current;

      // Unseen = newer than the watermark AND not already authoritatively
      // handled this session. We check `resolvedRef` ONLY (NOT `timedOutRef`):
      // a hub-delivered authoritative terminal for a timed-out id MUST settle
      // and promote it (CT-3). The `inFlightRef` filter stays — it dedupes
      // while the watcher is actively polling; after a `timeout` the id is
      // cleared from `inFlight`, so hub delivery is not filtered here (CT-5).
      const unseen = res.notifications.filter((n) => {
        if (watermark != null && !(n.at > watermark)) return false;
        if (resolvedRef.current.has(n.requestId)) return false;
        if (inFlightRef.current.has(n.requestId)) return false;
        return true;
      });

      // Map a notification's three-way `outcome` to the authoritative settle
      // outcome (CT-5). `live` completions carry a fuelPlanId.
      const settleNotification = (n: NotificationsResponse["notifications"][number]) => {
        const outcome: "completed" | "superseded" | "failed" =
          n.outcome === "live" ? "completed" : n.outcome === "superseded" ? "superseded" : "failed";
        const toast = resolveTerminalOutcome(n.outcome, {
          fuelPlanId: n.fuelPlanId,
          label: n.label,
          failureSummary: n.failureSummary,
          navigate
        });
        // Route through the SHARED authoritative settle so a hub-delivered
        // completion doesn't just toast — it publishes to the bus + refetches
        // rows/journey, flipping the box CTA (CT-5, the linchpin). The settle's
        // own `resolvedRef` guard makes this idempotent vs. the live watcher.
        settleAuthoritativeTerminal(n.requestId, outcome, {
          fuelPlanId: n.fuelPlanId,
          failureSummary: n.failureSummary,
          toast: toast ? { ...toast, durationMs: 0 } : null
        });
      };

      // Rows the shopper already dismissed / clicked on the STOREFRONT banner
      // (lib/planNotificationAck.ts) still settle authoritatively (bus +
      // refetch + watermark) but never toast again — they've seen it, or are
      // looking at the plan right now after "View plan →".
      const acknowledged = unseen.filter((n) => isPlanNotificationAcknowledged(n.requestId));
      const toastable = unseen.filter((n) => !isPlanNotificationAcknowledged(n.requestId));
      for (const n of acknowledged) {
        const outcome: "completed" | "superseded" | "failed" =
          n.outcome === "live" ? "completed" : n.outcome === "superseded" ? "superseded" : "failed";
        settleAuthoritativeTerminal(n.requestId, outcome, {
          fuelPlanId: n.fuelPlanId,
          failureSummary: n.failureSummary,
          toast: null
        });
      }

      if (toastable.length > 0 && toastable.length <= 2) {
        for (const n of toastable) settleNotification(n);
      } else if (toastable.length >= 3) {
        // Collapse to one sticky toast — "updates", not "finished" (some may
        // have failed). Still settle EACH underlying row authoritatively (bus +
        // reload + resolvedRef) so every box CTA flips; suppress the per-row
        // toast so the collapsed summary is the only toast shown.
        push({
          tone: "info",
          message: `${toastable.length} fuel plan updates while you were away`,
          durationMs: 0,
          action: { label: "View plans", onClick: () => navigate("/plans") }
        });
        for (const n of toastable) {
          const outcome: "completed" | "superseded" | "failed" =
            n.outcome === "live"
              ? "completed"
              : n.outcome === "superseded"
                ? "superseded"
                : "failed";
          settleAuthoritativeTerminal(n.requestId, outcome, {
            fuelPlanId: n.fuelPlanId,
            failureSummary: n.failureSummary,
            toast: null
          });
        }
      }

      // Advance the watermark to the newest returned `at` (or the existing
      // cursor when the window was empty). Optimistic client-side + a short
      // retry so a failed POST can't re-toast everything next boot.
      const newestAt = res.notifications.reduce<string | null>(
        (max, n) => (max == null || n.at > max ? n.at : max),
        null
      );
      const advanceTo = newestAt ?? watermark;
      if (advanceTo && (watermarkRef.current == null || advanceTo > watermarkRef.current)) {
        watermarkRef.current = advanceTo;
      }
      if (advanceTo) {
        const csrfToken =
          authState.kind === "customer" || authState.kind === "anon"
            ? authState.csrfToken
            : null;
        if (csrfToken) {
          void markSeenWithRetry(advanceTo, csrfToken, refreshCsrf);
        }
      }

      if (unseen.length > 0) lastNonEmptyAtRef.current = Date.now();
      return unseen.length;
    },
    [authState, navigate, push, refreshCsrf, settleAuthoritativeTerminal]
  );

  const runDrain = useCallback(async () => {
    if (!principalKeyRef.current) return;
    lastDrainAtRef.current = Date.now();
    try {
      const res = await loadNotifications();
      processNotifications(res);
    } catch {
      // Background convenience — never break the app on a drain failure.
    }
  }, [processNotifications]);

  // Drain once auth resolves to a real principal; re-latch when the principal
  // changes (anon→customer merge, cross-tab sign-in).
  useEffect(() => {
    const principalKey = authPrincipalKey(authState);
    if (!principalKey) return;
    if (drainedPrincipalRef.current === principalKey) return;
    drainedPrincipalRef.current = principalKey;
    void runDrain();
  }, [authState, runDrain]);

  // Re-drain on visibility regain, throttled to ≥5 min since the last drain —
  // catches "built while I was on another device".
  useEffect(() => {
    if (typeof document === "undefined") return;
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (!principalKeyRef.current) return;
      if (Date.now() - lastDrainAtRef.current < DRAIN_THROTTLE_MS) return;
      void runDrain();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [runDrain]);

  // ── Watch flag (impl note #6) ────────────────────────────────────────────
  // While the single watcher has a pending build, publish a short-lived flag to
  // localStorage so same-origin surfaces that CAN'T see React state — the
  // storefront theme snippet (Phase 2b) and the future long-poll (Phase 4) —
  // know it's worth polling. Keyed on `hasPending` (history-discovered builds
  // included), NOT `inFlight` (explicit track()s only). Cleared when idle.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (hasPending) {
        window.localStorage.setItem(WATCH_FLAG_KEY, String(Date.now() + WATCH_FLAG_TTL_MS));
      } else {
        window.localStorage.removeItem(WATCH_FLAG_KEY);
      }
    } catch {
      // Private-mode / disabled storage — the flag is a convenience only.
    }
  }, [hasPending]);

  // ── Push transport: bounded long-poll (Phase 4 §4.2) ─────────────────────
  // A single `/wait` hold gives push-like latency for build outcomes without
  // SSE (the Shopify app proxy kills streaming). The loop runs WHILE a build is
  // pending (`hasPending`) OR within 10 min of the last non-empty drain
  // ("unseen recently"), guarded on a real principal. Each iteration parks a
  // 20s hold on the SAME monotonic watermark the drain advances; rows feed the
  // SHARED `processNotifications` path (dedupe by requestId means overlap with
  // the per-request watcher is harmless). On null/204 it just loops; on error
  // it swallows + backs off ~4s. The in-flight fetch is aborted on unmount and
  // whenever the active-condition flips false. The per-request status watcher
  // (useGeneratingPlans) STAYS as the correctness fallback.
  //
  // follow-up: relax the per-request watcher's cadence to 60s while this
  // long-poll is healthy (spec §4.2 optional) — deferred to avoid touching the
  // watcher's correctness; it belongs behind a flag threaded through
  // useGeneratingPlanWatcher, not a one-liner here.
  const hasPendingRef = useRef(hasPending);
  hasPendingRef.current = hasPending;
  useEffect(() => {
    const principalKey = authPrincipalKey(authState);
    if (!principalKey) return;
    // A foreground `timeout` (CT-3) clears `hasPending`/`inFlight` for that id,
    // but the build may still finish server-side up to the server processing
    // window (well past LONG_POLL_UNSEEN_TTL_MS). Keep the hub alive while any
    // timed-out id is still unresolved so the eventual authoritative completion
    // is delivered and settles (CT-5 hand-off). Ids are removed from
    // `timedOutRef` on authoritative settle / re-arm.
    const hasUnresolvedTimeout = () => timedOutRef.current.size > 0;
    // Only spin the loop up when there's a reason to (a pending build, a recent
    // non-empty drain, or an unresolved give-up); it self-sustains across
    // iterations via the refs.
    const shouldStart =
      hasPending ||
      hasUnresolvedTimeout() ||
      Date.now() - lastNonEmptyAtRef.current < LONG_POLL_UNSEEN_TTL_MS;
    if (!shouldStart) return;

    let cancelled = false;
    let controller: AbortController | null = null;

    const active = () =>
      !cancelled &&
      principalKeyRef.current === principalKey &&
      (hasPendingRef.current ||
        hasUnresolvedTimeout() ||
        Date.now() - lastNonEmptyAtRef.current < LONG_POLL_UNSEEN_TTL_MS);

    const loop = async () => {
      while (active()) {
        controller = new AbortController();
        try {
          const res = await waitForNotifications(watermarkRef.current, {
            holdMs: LONG_POLL_HOLD_MS,
            signal: controller.signal
          });
          if (!active()) break;
          // null/204 → nothing new during the hold; just loop.
          if (res) processNotifications(res);
        } catch (err) {
          if (isAbortError(err) || !active()) break;
          // Network blip / server restart mid-hold — back off, then continue.
          await new Promise((r) => setTimeout(r, LONG_POLL_BACKOFF_MS));
        }
      }
    };

    void loop();

    return () => {
      cancelled = true;
      controller?.abort();
    };
    // Re-evaluate the start condition when the principal or pending-state
    // changes; the loop itself is driven by the refs above.
  }, [authState, hasPending, timeoutEpoch, processNotifications]);

  // ── Visibility-driven history reload (§3.1) ──────────────────────────────
  // A build dispatched on another tab/device while this tab slept won't be in
  // our loaded history. On a visibility regain, reload the plan history
  // (throttled ≥60s) so the single watcher re-discovers it — replacing the
  // per-screen watchers that used to paper over this.
  const lastHistoryReloadRef = useRef(0);
  useEffect(() => {
    if (typeof document === "undefined") return;
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - lastHistoryReloadRef.current < HISTORY_RELOAD_THROTTLE_MS) return;
      lastHistoryReloadRef.current = Date.now();
      reloadRows();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [reloadRows]);

  const value = useMemo<PlanGenerationContextValue>(
    () => ({ track, inFlight, isPlanRegenerating, rows, reloadRows, justResolved, hasPending }),
    [track, inFlight, isPlanRegenerating, rows, reloadRows, justResolved, hasPending]
  );

  return <PlanGenerationContext.Provider value={value}>{children}</PlanGenerationContext.Provider>;
}
